import React, {
  createContext,
  useEffect,
  useLayoutEffect,
  useState,
  JSX,
} from 'react';
import { DateTime } from 'luxon';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { DateRanges } from '../utils/DateRanges';

interface IDateRangeOptions {
  startDate: string;
  endDate: string;
}

export interface ICommonContext {
  dateRangeOptions: IDateRangeOptions;
  setDateRangeOptions: (value: IDateRangeOptions) => void;
  showLoadingOverlay: boolean;
  setShowLoadingOverlay: (value: boolean) => void;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  timezone: string;
}

interface ICommonProvider {
  children: JSX.Element;
}

export const CommonContext = createContext<ICommonContext>({
  dateRangeOptions: {
    startDate: '',
    endDate: '',
  },
  setDateRangeOptions: () => null,
  showLoadingOverlay: false,
  setShowLoadingOverlay: () => null,
  isMobile: false,
  isTablet: false,
  isDesktop: false,
  timezone: '',
});

// Stays false on the server; flips to true after the first client hydration.
let hasHydrated = false;

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Returns false for the render that hydrates server HTML so breakpoint-driven
 * markup matches the server (which has no window width), then true before
 * paint. Client-side navigations start as true, so they don't re-render.
 */
const useHydrated = (): boolean => {
  const [hydrated, setHydrated] = useState<boolean>(hasHydrated);

  useIsomorphicLayoutEffect(() => {
    hasHydrated = true;
    setHydrated(true);
  }, []);

  return hydrated;
};

export const CommonProvider = ({ children }: ICommonProvider) => {
  const [dateRangeOptions, setDateRangeOptions] = useState<IDateRangeOptions>(
    DateRanges.YTD,
  );
  const [showLoadingOverlay, setShowLoadingOverlay] = useState<boolean>(false);

  const hydrated = useHydrated();
  const smBreakpoint = useBreakpoint('sm');
  const mdBreakpoint = useBreakpoint('md');
  const lgBreakpoint = useBreakpoint('lg');
  const isMobile: boolean = hydrated && smBreakpoint.lesser;
  const isTablet: boolean = hydrated && mdBreakpoint.exact;
  const isDesktop: boolean = hydrated && lgBreakpoint.greater;
  const timezone = DateTime.local().zone.name;

  return (
    <CommonContext.Provider
      value={{
        dateRangeOptions,
        setDateRangeOptions,
        showLoadingOverlay,
        setShowLoadingOverlay,
        isMobile,
        isTablet,
        isDesktop,
        timezone,
      }}
    >
      {children}
    </CommonContext.Provider>
  );
};

export default CommonProvider;
